import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { OPEN_SHIFTS } from "../../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest, RequestOrigins, ShiftRequest } from "../../../../utils/models";
import { hasNurseResponse } from "../../../../utils/utilFunctions";
import { RequestFilter } from "../../../RequestData/RequestsTab/utils";
import { JumpingDots } from "../../../UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import type { GapsRequestDayGroup } from "../gapsRequests";
import { GapsRequestCard } from "./GapsRequestCard";
import { GapsRequestDayHeader } from "./GapsRequestDayHeader";
import type { GapsApplicant } from "./useGapsApplicants";
import { GapsOpenShiftsLoad } from "./useGapsOpenShiftsLoad";

/**
 * The response tabs that open every answered card on arrival, so the people who asked are in view without a tap:
 * Answered, and Replace (APP-886), where who offered to take the shift over is the whole question. All and
 * Unanswered open only urgent claims.
 */
const opensAnsweredCards = (filter: RequestFilter): boolean => filter === RequestFilter.WithResponse || filter === RequestFilter.ReplaceRequest;

type Props = {
	load: GapsOpenShiftsLoad;
	/** The shown requests grouped by staffing day, earliest first. */
	days: GapsRequestDayGroup<ShiftRequest>[];
	/** What an empty list says: nothing posted, nothing matching the filters, or nothing in this response tab. */
	emptyTitle: string;
	isNarrowed: boolean;
	responseFilter: RequestFilter;
	/** Requests with a robo call already out. */
	roboCallRequestIds: ReadonlySet<string>;
	/** Why each request exists; empty where the department does not get origins. */
	originsByRequestId: ReadonlyMap<string, RequestOrigins>;
	now: DateTime;
	onOpenRequest: (request: OpenShiftRequest | ReplaceShiftRequest) => void;
	onApprove: (request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant) => void;
};

/**
 * Open Shifts' list: loading, a failed load, nothing to show, or the requests by staffing day — each day under its
 * band (the 48h band when it holds an urgent claim), each request a card. A failed or unsettled load is never drawn as
 * an empty list.
 */
export const GapsOpenShiftsList: FC<Props> = ({
	load,
	days,
	emptyTitle,
	isNarrowed,
	responseFilter,
	roboCallRequestIds,
	originsByRequestId,
	now,
	onOpenRequest,
	onApprove,
}) => {
	const { t } = useTranslation();
	if (load === GapsOpenShiftsLoad.Loading) {
		return (
			<Box data-testid="gaps-requests-loading" sx={{ display: "flex", justifyContent: "center", py: 6 }}>
				<JumpingDots />
			</Box>
		);
	}
	if (load === GapsOpenShiftsLoad.Failed) {
		return (
			<EmptyState
				dataTestId="gaps-requests-load-failed"
				icon={<ErrorOutlineOutlinedIcon />}
				iconColor="error.main"
				title={t("gaps.requests.loadFailed")}
				hint={t("gaps.requests.loadFailedHint")}
				sx={{ py: 6, px: 3 }}
			/>
		);
	}
	if (days.length === 0) {
		return (
			<EmptyState
				dataTestId="gaps-requests-empty"
				icon={<PendingActionsOutlinedIcon />}
				title={emptyTitle}
				hint={isNarrowed ? t("gaps.requests.emptyFilteredHint") : undefined}
				sx={{ py: 6, px: 3 }}
			/>
		);
	}
	return (
		<>
			{days.map((day) => {
				const urgentCount = day.entries.filter((entry) => entry.isUrgentClaim).length;
				return (
					<Box key={day.dayKey} component="section" data-testid={`gaps-requests-group-${day.dayKey}`}>
						<GapsRequestDayHeader group={day} urgentCount={urgentCount} shiftCount={day.entries.length} />
						<Box sx={{ display: "flex", flexDirection: "column", gap: OPEN_SHIFTS.CARD_GAP, px: OPEN_SHIFTS.LIST_INSET, pb: OPEN_SHIFTS.CARD_GAP }}>
							{day.entries.map((entry) => (
								<GapsRequestCard
									key={entry.request.id}
									request={entry.request}
									isUrgentClaim={entry.isUrgentClaim}
									isExpandedByList={entry.isUrgentClaim || (opensAnsweredCards(responseFilter) && hasNurseResponse(entry.request))}
									isRoboCallRunning={roboCallRequestIds.has(entry.request.id)}
									origins={originsByRequestId.get(entry.request.id) ?? null}
									now={now}
									onOpenRequest={onOpenRequest}
									onApprove={onApprove}
								/>
							))}
						</Box>
					</Box>
				);
			})}
		</>
	);
};
